import { useMemo } from 'react'

import { Skeleton } from '@mui/material'
import type { AbortResetClient } from 'commons/customHooks'
import { ensureNonNegative, formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import { Icon } from 'components'
import { TypesValidations } from 'constants/enums'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import FactoryStock from '../FactoryStock'
import * as S from './styles'

const ProductHeader = ({ resetController }: AbortResetClient) => {
  const navigate = useNavigate()
  const { orderNumber } = useParams()
  const {
    records,
    draftBody: { pc_dsct: orderDiscount },
  } = useSelector(({ salesOrder }) => salesOrder)
  const { materialSelected, matrixStock, loadingStock, defaultParams } = useSelector(
    ({ products }) => products,
  )
  const { preco: prices } = materialSelected

  const isOutOfLine = defaultParams.fl_matr_fora_linh === TypesValidations.YES

  const discount = useMemo(() => {
    const selectedItem = records.find(
      (record) => record.nr_item_pedi_rasc === materialSelected.nr_item_pedi_rasc,
    )
    if (!selectedItem?.pc_dsct) return orderDiscount
    return +selectedItem.pc_dsct
  }, [materialSelected])

  const hasDiscount = useMemo(() => {
    return discount > 0
  }, [discount])

  const noAnticipated = materialSelected?.fl_pedi_antp !== TypesValidations.YES

  const isMatStock = useMemo(
    () => /m/i.test(materialSelected?.tp_estq_exce) && noAnticipated,
    [materialSelected],
  )

  const goBack = () => {
    navigate(-1)
  }

  const renderMaterialComponent = () => {
    const { cd_matr, cd_matr_cota } = materialSelected

    if (cd_matr !== cd_matr_cota)
      return (
        <small>
          Cód: {cd_matr} / {cd_matr_cota}
        </small>
      )

    return <small>Cód: {cd_matr}</small>
  }

  return (
    <>
      <S.Container>
        <S.TitleRow>
          <S.Row>
            <S.ReturnButton id="btn-material-return" onClick={goBack}>
              <Icon color={theme.colors.primary.main} icon="chevron-left" size="20" />
            </S.ReturnButton>
            <S.MaterialName>
              {toAllFirstLetterUpper(materialSelected?.ds_matr)}
              {renderMaterialComponent()}
            </S.MaterialName>
          </S.Row>
          {orderNumber && (
            <S.OrderNumber>
              Pedido <strong>#{orderNumber}</strong>
            </S.OrderNumber>
          )}
        </S.TitleRow>

        <S.PricesRow>
          <S.InfoRow>
            <S.InfoCol>
              <S.InfoTitle>Preço Modelo Infantil</S.InfoTitle>
              <S.PricesCol>
                <S.ValueRow>
                  <S.Value>
                    {formatToPTBRNumber(prices.vl_prec_unit_mini * (1 - discount / 100))}
                    {hasDiscount && ' • '}
                  </S.Value>
                  {hasDiscount && <S.Discount>{discount}% de desconto</S.Discount>}
                </S.ValueRow>
                {hasDiscount && (
                  <S.Subtitle>
                    Preço padrão: <span>{formatToPTBRNumber(prices.vl_prec_unit_mini)}</span>
                  </S.Subtitle>
                )}
              </S.PricesCol>
            </S.InfoCol>

            <S.InfoCol>
              <S.InfoTitle>Preço Modelo Adulto</S.InfoTitle>
              <S.PricesCol>
                <S.ValueRow>
                  <S.Value>
                    {formatToPTBRNumber(prices.vl_prec_unit_maxi * (1 - discount / 100))}
                    {hasDiscount && ' • '}
                  </S.Value>
                  {hasDiscount && <S.Discount>{discount}% de desconto</S.Discount>}
                </S.ValueRow>
                {hasDiscount && (
                  <S.Subtitle>
                    Preço padrão: <span>{formatToPTBRNumber(prices.vl_prec_unit_maxi)}</span>
                  </S.Subtitle>
                )}
              </S.PricesCol>
            </S.InfoCol>

            {isMatStock && (
              <>
                <S.Divider />
                <S.InfoCol>
                  <S.InfoTitle>Estoque Total da Fábrica/Centro</S.InfoTitle>
                  <S.PricesRow>
                    <S.PricesCol>
                      {loadingStock ? (
                        <Skeleton animation="wave" height={19} width={70} />
                      ) : (
                        <S.ValueRow>
                          <S.Value>
                            {numberToDecimalString(
                              matrixStock?.totalDisponivel &&
                                ensureNonNegative(matrixStock?.totalDisponivel),
                            )}
                          </S.Value>
                        </S.ValueRow>
                      )}
                    </S.PricesCol>
                  </S.PricesRow>
                </S.InfoCol>
              </>
            )}
          </S.InfoRow>

          {isOutOfLine && <FactoryStock resetController={resetController} />}
        </S.PricesRow>
      </S.Container>

      {!isOutOfLine && <FactoryStock resetController={resetController} />}
    </>
  )
}

export default ProductHeader
